/** @jsxImportSource @emotion/react */
import {
  Copy,
  List,
  ListItem,
  Modal,
  Typography,
  useTheme,
} from '@quickbit/qb-design-system';
import _ from 'lodash';
import { TruncatedString } from 'components';
import { Order } from 'tabs/types';

interface Props {
  order: Order;
  allowBackofficeOrderCreation?: boolean;
  deselect: () => void;
}

const REACT_APP_QB_PAY_FRONTEND_BASE_URL =
  process.env.REACT_APP_QB_PAY_FRONTEND_BASE_URL;

export const OrderDetails = ({
  order,
  deselect,
  allowBackofficeOrderCreation,
}: Props) => {
  const { sizing, spacing } = useTheme();
  const paymentLink = `${
    REACT_APP_QB_PAY_FRONTEND_BASE_URL || 'INVALID_URL'
  }?order-id=${order.uuid}`;
  return (
    <Modal
      title="Order details"
      secondaryButtonProps={{
        onClick: deselect,
        text: 'Close',
      }}
      closeModal={deselect}
    >
      <div
        css={{
          overflow: 'scroll',
          maxHeight: '80vh',
          minWidth: sizing.large,
        }}
      >
        <List>
          <ListItem
            title="Quickbit Order ID"
            subcontent={<TruncatedString text={order.uuid} />}
          />
          {order.referenceId && (
            <ListItem
              title="Reference ID"
              content={order.referenceId.toLocaleString()}
            />
          )}
          <ListItem
            title="Order amount"
            content={`${order.amount.toString()} ${order.currency}`}
          />
          <ListItem
            title="Creation amount"
            content={`${order.creationAmount.toString()} ${order.currency}`}
          />
          {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
          {order.createdAt && (
            <ListItem
              title="Created At"
              content={order.createdAt.toLocaleString()}
            />
          )}
          {order.completedAt && (
            <ListItem
              title="Completed At"
              content={order.completedAt.toLocaleString()}
            />
          )}
          <ListItem title="Status" content={order.externalStatus} />
          {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
          {order.extraData && (
            <>
              {Object.entries(order.extraData)
                .filter(([, value]) => value !== null)
                .map(([key, value]) => (
                  <ListItem
                    key={key}
                    title={_.kebabCase(key)
                      .split('-')
                      .map((word) => _.capitalize(word))
                      .join(' ')}
                    content={
                      typeof value === 'string' ? value : JSON.stringify(value)
                    }
                  />
                ))}
            </>
          )}
          {order.useOnrampFlow && (
            <ListItem
              title="Was Auto Logged In"
              subtitle="Whether User Data was provided on order creation"
              content={order.hasUserData ? 'Yes' : 'No'}
            />
          )}
          {!order.useOnrampFlow &&
            !order.useProcessingFlow &&
            allowBackofficeOrderCreation && (
              <ListItem
                title="Checkout URL"
                subcontent={
                  <div css={{ display: 'flex', marginLeft: spacing.m }}>
                    <Typography variant="bodySmall">{paymentLink}</Typography>
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <Copy textToCopy={paymentLink} />
                    </div>
                  </div>
                }
              />
            )}
        </List>
      </div>
    </Modal>
  );
};
